import { useState } from "react";
import { Box } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useNavigateWithParams } from "../../../hooks/useNavigateWithParams";
import SubscriptionCGV from "../../organisms/SubscriptionCGV/SubscriptionCGV";
import SubscriptionChoice from "../../organisms/SubscriptionChoice/SubscriptionChoice";
import { useAppDispatch, useAppSelector } from "../../../rtk/_config/hooks";
import { config as appConfig } from "../../../config";
import { setBasket } from "../../../rtk/basket/basket";
import { RootState } from "../../../types/rtk/api-config/types";
import { isAppSource } from "../../../helpers/getSourceFromUrl";
import Footer from "../../templates/Footer/Footer";
import Header from "../../templates/Header/Header";
import StyledButton from "../../atoms/StyledButton/StyledButton";
import { AppRoutes } from "../../../routes/AppRoutes";
import SpinnerLoader from "../../atoms/SpinnerLoader/SpinnerLoader";
import ModalPromotionalCode from "../../molecules/ModalSubscriptionGiftCard/ModalSubscriptionGiftCard";
import { useLazyPostPromoQuery } from "../../../rtk/promo/promo";
import { promoRequest } from "../../../types/rtk/promo/promo";
import { useGetOffersQuery } from "../../../rtk/offers/offers";
import PopIn from "../../molecules/PopIn/PopIn";

const Subscriptions = () => {
  const { t } = useTranslation();
  const navigate = useNavigateWithParams();
  const dispatch = useAppDispatch();
  const basket = useAppSelector((state: RootState) => state.basket);
  const data = useAppSelector((state: RootState) => state.offers);
  const { isLoading: offersLoading } = useGetOffersQuery();


  // Gift card management
  const [sendGiftCard] = useLazyPostPromoQuery();
  const [isModalOpenGiftCard, setIsModalOpenGiftCard] = useState(false);

  const handleOpenGiftCard = () => setIsModalOpenGiftCard(true);
  const handleCloseGiftCard = () => setIsModalOpenGiftCard(false);

  const handleValidateGiftCard = async (giftcard: string | null) => {
    const giftcard_data: promoRequest = { giftcard: giftcard };
    const resp = await sendGiftCard({ body: giftcard_data });
    if (resp.isError) return false;
    dispatch(setBasket({ 
      ...basket,
      offer_id: resp.data?.offer_id || null, 
      giftcard: resp.data?.giftcard || null,
      giftcard_ratio: resp.data?.ratio || null,
      promo: null,
      promocode_ratio: null
    }));
    setIsModalOpenGiftCard(false);
    return true;
  };

  // Deterrent pop in management
  const [deterrentPopIn, setDeterrentPopIn] = useState(false);

  // Next button management
  const handleNext = () => {
    const selectedOffer = data.subscriptionsData.find(offer => offer.id === basket.offer_id);
    const yearlyOffer = data.subscriptionsData.find(offer => offer.period === 12);
    if (selectedOffer?.period === 1 && yearlyOffer) {
      setDeterrentPopIn(true);
      return;
    }
    navigate(AppRoutes.OPTIONS);
  };

  const handleSwitchToYearly = () => {
    const yearlyOffer = data.subscriptionsData.find(offer => offer.period === 12);
    if (yearlyOffer) {
      dispatch(setBasket({ ...basket, offer_id: yearlyOffer.id }));
    }
    setDeterrentPopIn(false);
    navigate(AppRoutes.OPTIONS);
  };

  return (
    <>
      <Header
        headerStep={{ context: "new_subscription", step: 1, totalSteps: 2 }}
        title={t("subscription:HEADER.TITLE.FIRST_PAGE")}
        goBack={isAppSource() ? undefined : () => { window.location.href = appConfig.REACT_APP_FE_BASE_URL }}
      />
      <Box className="Subscription">
        {offersLoading ?  <SpinnerLoader/> : <SubscriptionChoice datas={data} handleOpenGiftCard={handleOpenGiftCard} />}
        <SubscriptionCGV />
      </Box>
      <Footer headerStep={{ context: "new_subscription", step: 1, totalSteps: 2 }}>
        <StyledButton
          children={offersLoading ? <SpinnerLoader/> : t("subscription:FIRST_PAGE.BUTTON")}
          onClick={handleNext}
          disabled={offersLoading || (basket.offer_id === null)}
        />
      </Footer>
      <ModalPromotionalCode
        isModalOpen={isModalOpenGiftCard}
        title={t("subscription:FIRST_PAGE.MODAL_GIFT_CARD.TITLE")}
        current={basket.giftcard}
        handleClose={handleCloseGiftCard}
        handleValidate={handleValidateGiftCard}
        mode="giftCard"
      />
      <PopIn
        isOpen={deterrentPopIn}
        onClose={() => setDeterrentPopIn(false)}
        title={t("subscription:FIRST_PAGE.DETERRENT_MONTHLY_POPIN.TITLE")}
        description={t("subscription:FIRST_PAGE.DETERRENT_MONTHLY_POPIN.DESCRIPTION")}
        buttonLabel={t("subscription:FIRST_PAGE.DETERRENT_MONTHLY_POPIN.BUTTON.CHANGE")}
        onButtonClick={handleSwitchToYearly}
        secondButtonLabel={t("subscription:FIRST_PAGE.DETERRENT_MONTHLY_POPIN.BUTTON.CONTINUE")}
        onSecondButtonClick={() => {
          setDeterrentPopIn(false);
          navigate(AppRoutes.OPTIONS);
        }}
      />
    </>
  );
};

export default Subscriptions;
